import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Landmark, RotateCcw } from "lucide-react";
import {
  buildPublicSectorDashboard,
  SECTOR_ORDER,
  SECTOR_RESTITUTION_DEFAULTS,
} from "@/lib/simulator";
import { fmtMoney, fmtDays } from "@/lib/chart-utils";

const SEVERITY_STYLES = {
  green: {
    badge:
      "bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-800",
    label: "Retour > 100%",
  },
  orange: {
    badge:
      "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-800",
    label: "Retour 50–100%",
  },
  red: {
    badge:
      "bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-950/50 dark:text-rose-200 dark:border-rose-800",
    label: "Retour < 50%",
  },
};

export default function PublicSectorDashboard({ analysis }) {
  const results = analysis?.results || {};
  const currency = results.summary?.currency || "UM";
  const budgetLines = results.aggregated_budget_lines || [];
  const salaireJournalierMoyen =
    results.national?.salaire_journalier_moyen ||
    results.debt?.salaire_journalier_moyen ||
    results.profiles?.[0]?.individual?.salaire_journalier ||
    0;
  const populationActive =
    results.debt?.population_active ||
    analysis?.inputs?.population_active ||
    1_000_000;

  const [overrides, setOverrides] = useState({ ...SECTOR_RESTITUTION_DEFAULTS });

  const rows = useMemo(
    () =>
      buildPublicSectorDashboard({
        aggregatedBudgetLines: budgetLines,
        salaireJournalierMoyen,
        populationActive,
        restitutionOverrides: overrides,
      }),
    [budgetLines, salaireJournalierMoyen, populationActive, overrides]
  );

  const reset = () => setOverrides({ ...SECTOR_RESTITUTION_DEFAULTS });

  const noData = !budgetLines.length || !salaireJournalierMoyen;

  return (
    <div className="space-y-5" data-testid="public-sector-dashboard">
      <Card
        className="border-brand-blue/30 bg-brand-blue/5 dark:border-brand-gold/30 dark:bg-brand-gold/5"
        data-testid="sector-intro"
      >
        <CardContent className="flex items-start gap-3 p-4">
          <Landmark className="mt-0.5 h-5 w-5 text-brand-blue dark:text-brand-gold" />
          <div className="text-sm">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              Tableau de bord du gestionnaire public
            </div>
            <p className="mt-1">
              Chaque secteur consomme un volume de{" "}
              <strong>vie investie</strong> (jours de vie par contribuable).
              Le <strong>ratio vitaliste</strong> estime la vie restituée aux
              citoyens via la qualité du service. Ajustez les ratios ci-dessous
              pour refléter votre lecture du terrain — les valeurs par défaut
              sont des repères indicatifs, transparents et éditables.
            </p>
          </div>
        </CardContent>
      </Card>

      {noData ? (
        <Card className="border-dashed">
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Données budgétaires ou salariales insuffisantes pour calculer les
            jours de vie investis par secteur.
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="sector-table-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">
              Valeur-vie investie vs restituée
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={reset}
              data-testid="sector-reset-btn"
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
              Valeurs par défaut
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sector-table">
                <thead className="border-b border-border bg-muted/40">
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="p-3 font-semibold">Secteur</th>
                    <th className="p-3 font-semibold">
                      Valeur-vie investie (jours/personne)
                    </th>
                    <th className="p-3 font-semibold">
                      Valeur-vie restituée (jours/personne)
                    </th>
                    <th className="p-3 font-semibold">Indicateur vitaliste</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const severityStyle =
                      SEVERITY_STYLES[row.severity] || SEVERITY_STYLES.red;
                    return (
                      <tr
                        key={row.sector}
                        className="border-b border-border last:border-0 align-top"
                        data-testid={`sector-row-${row.sector}`}
                      >
                        <td className="p-3">
                          <div className="font-semibold">{row.sector}</div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            Budget total :{" "}
                            <span className="font-mono">
                              {fmtMoney(row.total_um, currency)}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 font-mono">
                          {fmtDays(row.days_invested)}
                        </td>
                        <td className="p-3 font-mono">
                          {fmtDays(row.days_restituted)}
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col gap-1.5">
                            <Badge
                              variant="outline"
                              className={`w-fit border ${severityStyle.badge}`}
                              data-testid={`sector-badge-${row.sector}`}
                            >
                              {row.ratio_pct}% — {severityStyle.label}
                            </Badge>
                            <div className="mt-1 w-full">
                              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Ajuster le ratio restitué
                              </Label>
                              <Slider
                                min={0}
                                max={200}
                                step={5}
                                value={[Math.round(row.ratio * 100)]}
                                onValueChange={(v) =>
                                  setOverrides((prev) => ({
                                    ...prev,
                                    [row.sector]: Number(v[0]) / 100,
                                  }))
                                }
                                data-testid={`sector-slider-${row.sector}`}
                                className="mt-1"
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="rounded-md border border-dashed border-border bg-muted/20 p-3 text-xs text-muted-foreground">
        <strong>Notes méthodologiques :</strong> Les secteurs sont agrégés par
        mots-clés sur les postes budgétaires agrégés (ex. "santé" → Santé,
        "défense/police" → Sécurité). Le ratio restitué est par défaut{" "}
        {Object.entries(SECTOR_RESTITUTION_DEFAULTS)
          .map(([s, v]) => `${s}: ${Math.round(v * 100)}%`)
          .join(" · ")}
        . Code couleur : vert ≥ 100%, orange 50–100%, rouge &lt; 50%.
      </div>
    </div>
  );
}
